import { audioBufferCache } from './audioBufferCache'
import { getAudioContext } from './audioContext'

const inFlight = new Map<string, Promise<AudioBuffer>>()

// Downloaded audio lives in this Cache Storage namespace (written by
// offlineStore). Read it DIRECTLY instead of relying on the service worker to
// serve it: the SW's range/Vary handling of cached media was dropping requests
// mid-playlist when offline, so playback stopped at the first file the SW
// failed to return. `ignoreVary` defeats a Vary-header cache miss.
const OFFLINE_AUDIO_CACHE = 'qlearn-offline-audio-v1'

async function offlineCachedResponse(url: string): Promise<Response | null> {
  if (typeof caches === 'undefined') return null
  try {
    const cache = await caches.open(OFFLINE_AUDIO_CACHE)
    const hit = await cache.match(url, { ignoreVary: true })
    return hit && hit.ok ? hit : null
  } catch {
    return null
  }
}

export async function fetchAndDecode(url: string): Promise<AudioBuffer> {
  const cached = audioBufferCache.get(url)
  if (cached) return cached

  const existing = inFlight.get(url)
  if (existing) return existing

  const promise = (async () => {
    // We intentionally don't use `cache: 'force-cache'`. The browser would
    // happily serve back an old text/html SPA-fallback response that got
    // cached during a moment the dev proxy was misconfigured — defeating
    // every subsequent attempt to load the real MP3. In production the SW's
    // CacheFirst rule for /audio/* gives the perf win without this trap.
    // Prefer the explicitly-downloaded copy (offline-safe); fall back to the
    // SW/network for anything not downloaded.
    const response = (await offlineCachedResponse(url)) ?? (await fetch(url))
    if (!response.ok) {
      throw new Error(`Audio fetch failed for ${url}: ${response.status}`)
    }
    // Catch the common production failure mode where a CDN/proxy serves an
    // HTML or JSON error page with 200 OK — decodeAudioData would otherwise
    // throw the generic "Unable to decode audio data" with no clue why.
    const contentType = (response.headers.get('content-type') || '').toLowerCase()
    if (contentType.startsWith('text/html') || contentType.startsWith('application/json')) {
      throw new Error(`Audio URL returned non-audio content (${contentType}) for ${url}`)
    }
    const arrayBuffer = await response.arrayBuffer()
    const ctx = getAudioContext()
    let buffer: AudioBuffer
    try {
      buffer = await ctx.decodeAudioData(arrayBuffer)
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err)
      throw new Error(`decodeAudioData failed for ${url} (type="${contentType}", bytes=${arrayBuffer.byteLength}): ${reason}`)
    }
    audioBufferCache.set(url, buffer)
    return buffer
  })()
    .finally(() => {
      inFlight.delete(url)
    })

  inFlight.set(url, promise)
  return promise
}

// Test-only reset
export function _resetInFlightForTests(): void {
  inFlight.clear()
}
